// Pointer-event plumbing that turns a drag into calls on the TransitionController. Unified across touch and
// mouse via Pointer Events. Two things it owns that the controller deliberately doesn't:
//   1. Intent — where a gesture is allowed to start (edge strip vs anywhere) and whether an early move is a
//      horizontal transition or a vertical scroll (which we must let through).
//   2. Pointer → progress math — including seamless re-grab of a settling animation, where the new baseline
//      is the CURRENT progress so the pane doesn't jump.
//   3. Arbitration between a gesture and a TAP, on a surface whose incoming pane is interactive
//      (`interactiveIncoming`): the same press can be either, so a drag swallows the click it would otherwise
//      fire, and a catch that stays still keeps the one it should.

import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'
import { TRANSITION_CONFIG } from '../core/motion.config'
import { swipeTargets, type RoutePath, type RouteTransition, type TransitionEdge } from './surface.config'
import { rtlog } from '../core/debug'
import { transitionsSuppressed } from '../core/motion'
import type { TransitionController } from './transition-controller'

const { edgeWidth, intentSlop } = TRANSITION_CONFIG

/**
 * Which swipe DIRECTIONS have already been spoken for on a given pointerdown, so nested transition surfaces
 * can share one gesture without fighting over it.
 *
 * Keyed by the event itself, so there is no state to clean up and no way to leak a stale claim: a pointerdown
 * bubbles from the innermost surface outwards, so the inner one always runs first and gets first refusal.
 * Crucially it claims only the directions it can actually SERVE — on the first admin tab there is no previous
 * tab, so a right-swipe goes unclaimed, falls through to the root surface, and pops you back to /request. On a
 * middle tab both directions are claimed and the swipe moves between tabs.
 */
const claims = new WeakMap<Event, Set<TransitionEdge>>()

function claimEdges(event: Event, wanted: readonly TransitionEdge[]): TransitionEdge[] {
  let taken = claims.get(event)
  if (!taken) {
    taken = new Set()
    claims.set(event, taken)
  }
  const granted: TransitionEdge[] = []
  for (const edge of wanted) {
    if (taken.has(edge)) continue
    taken.add(edge)
    granted.push(edge)
  }
  return granted
}

interface EdgeSwipeArgs {
  rootRef: RefObject<HTMLElement | null>
  controllerRef: RefObject<TransitionController | null>
  /** False inside a live preview: that tree is rendered to be LOOKED at, so it listens for nothing. */
  enabled: boolean
  /** The ordered group this surface swipes through. A swipe steps one member. */
  group: readonly RoutePath[]
  /** Start the gesture only from the `edgeWidth` strip (iOS back-swipe style) rather than anywhere. */
  edgeOnly?: boolean
  /** This surface lets the arriving page be touched mid-transition (see TransitionOutletProps). It changes two
   *  things HERE, both about the same thing — a press that lands on a live pane must not do two jobs at once. */
  interactiveIncoming?: boolean
  /** Current committed pathname, read fresh at gesture start (avoids stale closures). */
  getPathname: () => string
  /** Warm the target route's chunk + data on pointer-down, so committing it on release paints instantly. */
  preload: (to: RoutePath) => void
  /** The pane to slide in for `to`: a live render of it (see route-preview), or a snapshot fallback. */
  incomingFor: (to: RoutePath) => { layer: HTMLElement; applyScroll: () => void; managed?: boolean } | null
}

/** Is `x` (px from the root's left edge, width `w`) inside the start strip for a transition on `edge`? */
function startsInEdge(t: RouteTransition, x: number, w: number, edgeOnly: boolean): boolean {
  if (!edgeOnly) return true // pager mode is the default: start anywhere
  return t.edge === 'left' ? x <= edgeWidth : x >= w - edgeWidth
}

export function useEdgeSwipe(args: EdgeSwipeArgs): void {
  const { rootRef, controllerRef, enabled, group, edgeOnly = false, interactiveIncoming = false } = args
  // The three FUNCTION props, mirrored into refs and read at event time.
  //
  // They must not be effect dependencies: a caller re-rendering with a fresh closure would tear down and
  // rebind every pointer listener — mid-gesture, if the re-render was caused by the gesture. Mirrored rather
  // than suppressed, because React Compiler skips optimizing ANY function containing a React rule suppression
  // (it matches the directive text, so even quoting one in a comment re-triggers it). Mirroring says the same
  // thing to both tools, truthfully: the deps really are complete.
  const getPathnameRef = useRef(args.getPathname)
  const preloadRef = useRef(args.preload)
  const incomingForRef = useRef(args.incomingFor)
  // In a layout effect, never in render (see the same idiom in scroll-transition-outlet).
  useLayoutEffect(() => {
    getPathnameRef.current = args.getPathname
    preloadRef.current = args.preload
    incomingForRef.current = args.incomingFor
  })

  useEffect(() => {
    const root = rootRef.current
    if (!root || !enabled) return

    /**
     * ONE CLICK TO SWALLOW, armed by a gesture that actually travelled.
     *
     * A drag that starts and ends inside the incoming pane still fires a native `click` on release: the click
     * target is the nearest common ancestor of pointerdown and pointerup, and 200px of travel in between does not
     * change it. With the pane `inert` that was impossible; interactive, a completed swipe would "tap" whatever
     * button it happened to start on. So the release of a MOVED gesture eats exactly one click.
     *
     * Capture phase on the root, which is what makes it total: stopping propagation before the event reaches the
     * target means it never bubbles back up to React's delegated listener at the app container either, so no
     * onClick anywhere in the pane runs. Cleared by the click it eats, and again on the next pointerdown, so a
     * gesture whose click never arrives (touch, or a release outside any element) can't eat a later real one.
     */
    let swallowClick = false
    const onClickCapture = (e: Event) => {
      if (!swallowClick) return
      swallowClick = false
      e.stopPropagation()
      e.preventDefault()
      rtlog('swallow-click')
    }

    // Per-gesture mutable state. `active` holds the locked-in transition once a drag has begun (or a
    // settle has been re-grabbed); `pending` holds a not-yet-decided press still choosing intent.
    let pending: { startX: number; startY: number; candidates: RouteTransition[] } | null = null
    let active: { transition: RouteTransition; sign: -1 | 1; baseX: number; baseProgress: number } | null = null
    let pointerId: number | null = null
    let lastX = 0
    let lastT = 0
    let velocityX = 0

    // The stage's width, measured ONCE per press instead of on every move. `getBoundingClientRect` forces a
    // synchronous layout flush, and the drag path called it on every single pointermove — while the panes were
    // being transformed on those same frames, so layout was always dirty and the flush always paid full price.
    // The stage cannot resize under a finger, so one measurement per gesture is all it can possibly need.
    let stageWidth = 0
    const rectWidth = () => stageWidth || 1
    // Capture is a nice-to-have (routes move events to root); release is guaranteed by the window listeners
    // below regardless. It can throw ("no active pointer") in transient states — never let that abort the drag.
    const capture = (id: number) => {
      try {
        root.setPointerCapture(id)
      } catch {
        /* capture optional — window pointerup/cancel still end the gesture */
      }
    }

    // `fresh` says whether this press STARTS a transition (arm the panes) or merely takes over one already in
    // flight (a re-grab — the panes exist, we just re-anchor them to the finger). Never infer it from
    // `baseProgress === 0`: a transition can legitimately be caught at progress 0, and arming again there
    // would mount a second incoming pane over the live page.
    const beginActive = (transition: RouteTransition, clientX: number, baseProgress: number, fresh: boolean) => {
      const controller = controllerRef.current
      if (!controller) return null
      active = {
        transition,
        sign: transition.edge === 'right' ? -1 : 1,
        baseX: clientX,
        baseProgress,
      }
      if (
        fresh &&
        !controller.begin(transition, getPathnameRef.current(), () => incomingForRef.current(transition.to))
      ) {
        active = null // it refused to arm (no live pane for the destination) — don't drag against nothing
        return null
      }
      return active
    }

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return
      if (transitionsSuppressed()) return
      const controller = controllerRef.current
      if (!controller) return

      // STALE-GESTURE RECOVERY. A pointerdown arriving while we still hold a gesture (`active`/`pending`), or
      // while the controller is stuck mid-drag, means a previous release was LOST — the browser only fires
      // pointerdown for a pointer that isn't already down, so the old one is gone. Abandon it and reset to a
      // clean stage so THIS press starts fresh. This is what unsticks the frozen state on the next touch.
      // (An in-flight transition is NOT stale — it gets caught below, not reset.)
      if (active || pending || (controller.getState() === 'dragging' && !active)) {
        active = null
        pending = null
        pointerId = null
        controller.forceReset()
      }

      // A new press: whatever the last gesture armed, its click either arrived or never will.
      swallowClick = false
      pointerId = e.pointerId
      lastX = e.clientX
      lastT = e.timeStamp
      velocityX = 0
      // ONE rect read per press (it forces a layout flush): the width feeds the whole drag's math, the left
      // edge only the start-strip check below.
      const rect = root.getBoundingClientRect()
      stageWidth = rect.width || 1

      // DYNAMIC GRAB — the mobile behaviour. Put a finger on a transition that is still moving (or even one
      // already committing) and it stops dead under you: from here you can hold it still, drag it either way,
      // and let go, and it resolves from wherever you left it. It doesn't matter how the slide started — a
      // drag, a Link click, the back button — the controller reconciles the router with the end you drop it
      // on, so the address bar always agrees with what you see.
      // Catching a transition of OURS takes the whole pointer — both directions — so an outer surface can't
      // also start one on the same press. (An inner surface can't be running one here: while it is, the pane
      // above it is inert and its live layer has pointer-events:none, so the press lands on us anyway.)
      if (controller.canRegrab() && claimEdges(e, ['left', 'right']).length === 2) {
        const t = controller.getActiveTransition()
        if (t) {
          const at = controller.getProgress()
          controller.interrupt()
          capture(e.pointerId)
          beginActive(t, e.clientX, at, false)
          rtlog('regrab', { at: Math.round(at * 100) / 100 })
          root.style.userSelect = 'none'
          document.body.style.userSelect = 'none'
          // Normally the catch cancels the press's default actions outright. It must NOT on an interactive
          // surface: on touch, `preventDefault` on pointerdown suppresses the compatibility mouse events —
          // including the `click` — so a press that catches a settling transition and turns out to be a TAP would
          // stop the pane dead and then do nothing at all. Nothing else here depends on it (text selection is
          // handled by `user-select: none` just above, native image/link dragging by the dragstart handler), and
          // a catch that goes on to DRAG swallows its click on release instead.
          if (!interactiveIncoming) e.preventDefault()
          return
        }
      }

      // Fresh press: the transitions whose start strip contains this point AND whose direction a nested
      // surface hasn't already taken. Claiming per-DIRECTION is what lets surfaces nest — see `claims`.
      const w = rectWidth()
      const x = e.clientX - rect.left
      const offered = swipeTargets(group, getPathnameRef.current()).filter((t) => startsInEdge(t, x, w, edgeOnly))
      if (offered.length === 0) return
      const granted = new Set(
        claimEdges(
          e,
          offered.map((t) => t.edge),
        ),
      )
      const candidates = offered.filter((t) => granted.has(t.edge))
      if (candidates.length === 0) return
      pending = { startX: e.clientX, startY: e.clientY, candidates }
      for (const t of candidates) preloadRef.current(t.to)
    }

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      const controller = controllerRef.current
      if (!controller) return

      // A move with NO button pressed while we still believe a drag is running means the release was lost
      // (capture yanked, pointerup swallowed by another target). Recover on the spot: this is the cheap,
      // event-driven version of the old drag timeout, and unlike a timeout it can't mistake a deliberate hold
      // — a finger resting mid-swipe for ten seconds — for a wedged gesture.
      if (active && e.buttons === 0) {
        endGesture('buttons=0')
        return
      }

      const dt = e.timeStamp - lastT
      if (dt > 0) {
        const inst = (e.clientX - lastX) / dt
        velocityX = velocityX * 0.6 + inst * 0.4
      }
      lastX = e.clientX
      lastT = e.timeStamp

      if (active) {
        const p = active.baseProgress + (active.sign * (e.clientX - active.baseX)) / rectWidth()
        // Pushed PAST the destination, and there's another page beyond it in the same direction: chain onto it
        // rather than jamming against progress 1. This is what lets you keep flipping — grab a transition that
        // is still settling, keep dragging the same way, and you carry on to the page after it instead of
        // having to wait for the first one to land. The overshoot is carried into the new transition, so the
        // pane stays exactly under the finger across the handover.
        if (p > 1) {
          const onward = swipeTargets(group, active.transition.to).find((t) => t.edge === active?.transition.edge)
          if (onward && controller.chain(onward, () => incomingForRef.current(onward.to))) {
            active = { transition: onward, sign: active.sign, baseX: e.clientX, baseProgress: p - 1 }
            controller.setProgress(p - 1)
            e.preventDefault()
            return
          }
        }
        controller.setProgress(p)
        // Past the slop the controller judges by, this press is a DRAG — so its release must not also read as a
        // tap on whatever it started on. Asking the controller keeps one threshold for both decisions.
        if (interactiveIncoming && controller.isFingerMoving()) swallowClick = true
        e.preventDefault()
        return
      }

      if (!pending) return
      const dx = e.clientX - pending.startX
      const dy = e.clientY - pending.startY
      const ax = Math.abs(dx)
      const ay = Math.abs(dy)

      // Intent resolution, built to keep a real finger's gesture alive. A finger never travels on a perfect
      // axis: it starts diagonally and wobbles, and a SLOW drag wobbles most. So we never *reject* a press —
      // rejecting on the first sample where `dy > dx` is exactly what made slow swipes feel dead (one stray
      // vertical pixel and the drag was over for the rest of the press). Instead we only ever *accept*, once
      // horizontal travel is decisive; until then the press stays pending and the next samples get a say.
      //
      // Nothing is hijacked from the page by waiting: the surface is `touch-action: pan-y`, so a genuine
      // vertical scroll is taken over by the browser, which cancels our pointer stream (pointercancel →
      // endGesture). Requiring horizontal to DOMINATE (ax > ay) keeps us out of the way of that scroll.
      if (ax < intentSlop || ax <= ay) return // not decisive yet — keep watching
      rtlog('intent-horizontal', { dx: Math.round(dx), dy: Math.round(dy) })
      // Horizontal: the drag direction picks the edge (left-drag → 'right'/forward, right-drag → 'left'/back).
      const wantedEdge = dx < 0 ? 'right' : 'left'
      const transition = pending.candidates.find((t) => t.edge === wantedEdge)
      const pressX = pending.startX
      pending = null
      if (!transition) {
        pointerId = null
        return
      }
      capture(e.pointerId)
      // Anchor to the ORIGINAL press point, not this lock point. Rebasing here would swallow the slop
      // distance, so the pane would sit still for the first few pixels and only then start moving — a dead
      // zone that reads as "it doesn't respond until a threshold". Anchored to the press point, the pane is
      // already exactly under the finger the instant intent is known, and stays 1:1 with it from there.
      // A drag across page content otherwise starts a native text selection / image-or-link drag, which
      // swallows the pointer stream mid-gesture (the drag just dies under the finger). Suppress both for the
      // duration of the gesture; cleared on release.
      root.style.userSelect = 'none'
      document.body.style.userSelect = 'none'

      const started = beginActive(transition, pressX, 0, true)
      if (started) controller.setProgress((started.sign * (e.clientX - pressX)) / rectWidth())
      // A fresh drag is moved by definition — intent is only decisive past the slop — so its release always has a
      // click to eat, whether it began on a live pane or on the page underneath.
      if (started && interactiveIncoming) swallowClick = true
      e.preventDefault()
    }

    // `why` is logged: a drag that ends for the wrong reason (the browser claiming the gesture, a dropped
    // capture) is invisible otherwise — you just see the pane spring away from a finger that's still down.
    const endGesture = (why: string) => {
      const controller = controllerRef.current
      if (active || pending) rtlog('end-gesture', { why, had: active ? 'active' : 'pending' })
      if (active && controller) controller.release(velocityX)
      active = null
      pending = null
      pointerId = null
      root.style.userSelect = ''
      document.body.style.userSelect = ''
    }

    // Belt-and-braces with `user-select: none`: an image or link under the finger can still start a native
    // HTML5 drag, which cancels our pointer stream. Never allow it to begin on this surface.
    const onDragStart = (e: Event) => e.preventDefault()

    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      endGesture('pointerup')
    }
    const onPointerCancel = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      endGesture('pointercancel')
    }
    // If capture is yanked away (OS gesture, scroll takeover) the pointerup may never arrive — treat losing it
    // as a release so an in-flight drag can't get stuck with its snapshot covering.
    //
    // `e.target !== root` is what makes this safe on TOUCH, and its absence is why the gesture died on a real
    // finger. Touch (unlike mouse) gives the element under the finger IMPLICIT pointer capture — that's some
    // div deep inside the route content. Our own `setPointerCapture(root)` at intent-lock therefore MOVES the
    // capture off that descendant, and the browser announces it with a `lostpointercapture` fired AT the
    // descendant, which bubbles straight up to root. Unscoped, we read our own capture handoff as the OS
    // stealing the pointer and cancelled the drag we had just begun. Only root losing ITS capture counts.
    const onLostCapture = (e: PointerEvent) => {
      if (e.pointerId !== pointerId || e.target !== root) return
      endGesture('lostpointercapture')
    }
    const onWindowBlur = () => endGesture('blur')

    root.addEventListener('pointerdown', onPointerDown)
    root.addEventListener('lostpointercapture', onLostCapture)
    root.addEventListener('dragstart', onDragStart)
    root.addEventListener('click', onClickCapture, { capture: true })
    // Move AND release listeners go on WINDOW, not root: with rapid gestures + route re-renders the pointer
    // capture can drop, after which both the moves and the `pointerup` land off the root element. Bound to
    // root we'd miss them — the pane would stop tracking the finger, and the gesture would wedge in
    // 'dragging' forever. Window sees every one of them; they no-op unless a gesture owns the pointer id.
    window.addEventListener('pointermove', onPointerMove, { passive: false })
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerCancel)
    // The window losing focus mid-drag (OS gesture, alt-tab, devtools) can swallow the release entirely.
    window.addEventListener('blur', onWindowBlur)
    return () => {
      root.removeEventListener('pointerdown', onPointerDown)
      root.removeEventListener('lostpointercapture', onLostCapture)
      root.removeEventListener('dragstart', onDragStart)
      root.removeEventListener('click', onClickCapture, { capture: true })
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerCancel)
      window.removeEventListener('blur', onWindowBlur)
    }
    // `group`/`edgeOnly` are captured by the handlers, so a change (the root group is role-aware — see
    // rootGroupFor) must rebind or a press would offer stale targets. Both are module constants per role, so
    // in practice this rebinds only when the viewer's role flips. The function props are read at event time
    // and deliberately excluded — rebinding every pointer listener because a caller re-rendered is exactly
    // what this hook must not do.
    //
    // The list is now COMPLETE — no suppression — because the function props are read through refs above.
    // The two refs are listed because the rule asks for them; a ref's identity is stable, so they never
    // actually trigger a rebind.
    //
    // NOT `useEffectEvent`, which is the other way to keep a value out of a dep array. React's own docs rule
    // it out here: an Effect Event may be called from inside an Effect, but explicitly NOT from an event
    // handler — and every reader above is exactly that, a DOM listener firing long after the effect body
    // returned. Effect Events also intentionally get a NEW identity every render, so they cannot be what a
    // long-lived listener closes over. The latest-ref pattern is the right tool for this shape, and it is
    // what `useEffectEvent` is built out of internally (a ref + a layout-effect write).
  }, [enabled, group, edgeOnly, interactiveIncoming, rootRef, controllerRef])
}
